import React, { useMemo } from 'react'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import moment from 'moment'
import { t } from '../../infrastructure/i18nextHelper'
import { CommentButton } from '../common/comment/commentButton'
import { ColumnDescriptor, SapChip } from '../common/customComponents'
import { DealListItem, DealListItemBase, DealStatus, GetMovementType, PricingPeriodListItem } from './dealModels'
import { MovementTypeNameFromMovementType } from '../stock/stockModels'
import { getMeansOfTransportationName, MovementMotIcon, MovementTypeIcon } from '../stock/_movementIcon'
import { getBooleanLabel } from '../common/filtersHelper'
import { DealStatusChip } from './dealEdit/statusChip'
import { DealListFilters } from './dealList'

export type UseDealList = ReturnType<typeof useDealList>

export function useDealList(
    classes, dealListFilters: DealListFilters, setListFilters: (filters: DealListFilters) => void,
    hasFeatureGenericPricing: boolean, hasFeatureProvitionalPricing: boolean, hasFeatureUpdateSapVolume: boolean,
    hasFeatureFormulaType: boolean,
    totalVolume: number | null, totalMvtQuantity: number | null,
    totalSapQuantity: number | null, totalQuantity: number | null, totalPricedDealVolume: number | null,
    totalPricedMovementVolume: number | null, totalPricedSapQuantity: number | null, totalPricedDealQuantity: number | null,
    hasOneCompany: boolean, isInternal: (type: string | null) => boolean, isPaperTransaction: () => boolean,
    updateSapQuantity: (dealId: string) => Promise<void>
) {
    const formatDate = (date: Date | null) => !!date ? moment(date).format('MM/DD/YY') : ''
    const formatCell = (isGreen: boolean, value: number | null) => (<span className={isGreen ? classes.positive : ''}>{value}</span>)
    const formatFromToDate = (date: Date | null): string => !!date ? moment(date).format("MM/DD/YY") : ''

    const getCommentColumn = (associationKeyType: string, trad: string, defaultGroup?: string, groupedBy?: string): ColumnDescriptor<DealListItem>[] =>
        [{
            preventRowClick: true,
            textAlign: 'center',
            name: t(`deals.table.${trad}`),
            groupedBy: groupedBy,
            defaultGroup: defaultGroup,
            type: associationKeyType,
            value: () => '',
            htmlFor: x => <CommentButton
                associationKey={`${associationKeyType}-${x.id}`}
                hideIfNoContent
                readOnly={associationKeyType === 'deal' && isInternal(x.type)} />
        }]

    const groupedHeaderColumns: ColumnDescriptor<DealListItemBase>[] = [
        { name: t('deals.label.company'), value: x => x.company, hidden: hasOneCompany },
        {
            name: t('deals.label.dealNumberAbr'), defaultGroup: 'header',
            value: x => x.dealNumber,
            columnFilter:
            {
                value: dealListFilters.dealNumber ?? '',
                onChange: (dealNumber: string) => setListFilters({ ...dealListFilters, dealNumber })
            },
            wrapTextMax: true, sortProperty: 'dealNumber'
        },
        {
            name: t('deals.label.referenceNumberAbr'), defaultGroup: 'header',
            value: x => x.referenceNumber,
            columnFilter:
            {
                value: dealListFilters.referenceNumber ?? '',
                onChange: (referenceNumber: string) => setListFilters({ ...dealListFilters, referenceNumber })
            },
            wrapTextMax: true, valueSubText: x => (x.site ?? '') + ' - ' + (x.productCode ?? '')
        },
        {
            name: t('deals.label.mvtType'), groupedBy: 'header', value: x => MovementTypeNameFromMovementType(GetMovementType(x.movementType ?? '')),
            htmlFor: x =>
                <span className={!!x.movementType && classes.iconsActive}>
                    <MovementTypeIcon movementType={GetMovementType(x.movementType ?? '')} />
                </span>
        },
        { name: t('deals.label.dealType'), groupedBy: 'header', value: x => x.type },
        {
            name: t('deals.label.termSpot'), groupedBy: 'header', value: x => x.termSpot, textAlign: 'right',
            htmlFor: x => (
                x.termSpot == 'Term'
                    ? <CheckBox className={classes.iconsActive} />
                    : <CheckBoxOutlineBlank />
            )
        },
        { name: t('deals.label.product'), groupedBy: 'header', value: x => x.productCode },
        { name: t('deals.label.site'), groupedBy: 'header', value: x => x.site, wrapTextMax: true },
        { name: t('deals.label.dutyStatusAbr'), groupedBy: 'header', value: x => x.dutyStatus },
        {
            name: t('deals.label.mot'), groupedBy: 'header', value: x => getMeansOfTransportationName(x.meanOfTransportation ?? ''),
            htmlFor: x =>
                <span className={!!x.meanOfTransportation && classes.iconsActive}>
                    <MovementMotIcon meanOfTransportation={x.meanOfTransportation ?? ''} />
                </span>
        },
        { name: t('deals.label.counterparty'), groupedBy: 'header', value: x => x.counterparty?.name ?? '', wrapTextMax: true },
        {
            name: t('deals.label.validityPeriodAbr'), defaultGroup: 'header',
            value: x => formatFromToDate(x.validFrom) + ' -> ' + formatFromToDate(x.validTo)
        },
        ...getCommentColumn('deal', 'comments', 'header')
    ]

    const headerColumns: ColumnDescriptor<DealListItemBase>[] = [
        { name: t('deals.label.company'), value: x => x.company, hidden: hasOneCompany },
        {
            name: t('deals.label.dealNumberAbr'),
            value: x => x.dealNumber,
            columnFilter:
            {
                value: dealListFilters.dealNumber ?? '',
                onChange: (dealNumber: string) => setListFilters({ ...dealListFilters, dealNumber })
            },
            wrapTextMax: true, sortProperty: 'dealNumber'
        },
        {
            name: t('deals.label.referenceNumberAbr'),
            value: x => x.referenceNumber,
            columnFilter:
            {
                value: dealListFilters.referenceNumber ?? '',
                onChange: (referenceNumber: string) => setListFilters({ ...dealListFilters, referenceNumber })
            },
            wrapTextMax: true
        },
        {
            name: t('deals.label.mvtType'), value: x => MovementTypeNameFromMovementType(GetMovementType(x.movementType ?? '')),
            htmlFor: x =>
                <span className={!!x.movementType && classes.iconsActive}>
                    <MovementTypeIcon movementType={GetMovementType(x.movementType ?? '')} />
                </span>
        },
        { name: t('deals.label.dealType'), value: x => x.type },
        {
            name: t('deals.label.termSpot'), value: x => getBooleanLabel(x.termSpot == 'Term'), textAlign: 'right',
            htmlFor: x => (
                x.termSpot == 'Term'
                    ? <CheckBox className={classes.iconsActive} />
                    : <CheckBoxOutlineBlank />
            )
        },
        { name: t('deals.label.product'), value: x => x.productCode },
        { name: t('deals.label.site'), value: x => x.site, wrapTextMax: true },
        { name: t('deals.label.dutyStatusAbr'), value: x => x.dutyStatus },
        {
            name: t('deals.label.mot'), value: x => getMeansOfTransportationName(x.meanOfTransportation ?? ''),
            htmlFor: x =>
                <span className={!!x.meanOfTransportation && classes.iconsActive}>
                    <MovementMotIcon meanOfTransportation={x.meanOfTransportation ?? ''} />
                </span>
        },
        { name: t('deals.label.counterparty'), value: x => x.counterparty?.name ?? '', wrapTextMax: true },
        {
            name: t('deals.label.validityPeriodAbr'),
            value: x => formatFromToDate(x.validFrom) + ' -> ' + formatFromToDate(x.validTo)
        },
        ...getCommentColumn('deal', 'comments', 'header')
    ]

    const sapColumns = <T extends DealListItemBase>(): ColumnDescriptor<T>[] => [
        {
            name: t('deals.label.sapText'), defaultGroup: 'sapDetails',
            value: x => x.contractReference ?? '',
            htmlFor: x =>
            (!isPaperTransaction()
                ? <div>
                    <SapChip
                        className='dealList'
                        label={x.contractReference ?? 'PC'}
                        hasValue={!!x.contractReference}
                        isFromSap={x.isContractFromSap}
                        disabled={!x.contractReference && hasFeatureUpdateSapVolume}
                        clickable={hasFeatureUpdateSapVolume}
                        onClick={async (event) => {
                            if (!hasFeatureUpdateSapVolume) return
                            event.stopPropagation()
                            await updateSapQuantity(x.id)
                        }}
                    />
                </div>
                : <></>
            ),
            tooltipValue: (x) => x.contractReference && hasFeatureUpdateSapVolume ? t('deals.label.updateSapQuantity') : '',
        },
        { name: t('deals.label.exchangeNumberAbr'), groupedBy: 'sapDetails', value: x => x.exchangeNumber },
        { name: t('deals.label.sapPlant'), groupedBy: 'sapDetails', value: x => x.sapPlant },
        { name: t('deals.label.das'), groupedBy: 'sapDetails', value: x => x.das },
        { name: t('deals.label.sapHandlingType'), groupedBy: 'sapDetails', value: x => x.sapHandlingType },
        { name: t('deals.label.sapValuationType'), groupedBy: 'sapDetails', value: x => x.sapValuationType },
        { name: t('deals.label.sapVendor'), groupedBy: 'sapDetails', value: x => x.sapVendor },
        { name: t('deals.label.shipTo'), groupedBy: 'sapDetails', value: x => x.shipTo },
    ]

    const organizationnalColumns = <T extends DealListItemBase>(): ColumnDescriptor<T>[] => [
        {
            name: t('deals.label.status'), defaultGroup: 'organizationnalDetails', value: x => x.status,
            htmlFor: x => <DealStatusChip key={x.id} status={x.status as DealStatus} />
        },
        {
            name: t('deals.label.signed'), groupedBy: 'organizationnalDetails',
            value: x => getBooleanLabel(x.signed), textAlign: 'right',
            htmlFor: x => (
                x.signed
                    ? <CheckBox />
                    : <CheckBoxOutlineBlank />
            )
        },
        {
            name: t('deals.label.assigneeUserName'), groupedBy: 'organizationnalDetails',
            value: x => x.assigneeFullName, wrapTextMax: true, tooltipValue: x => x.assigneeFullName
        },
        {
            name: t('deals.label.frontOfficerAbr'), groupedBy: 'organizationnalDetails',
            value: x => x.frontOfficerFullName, wrapTextMax: true, tooltipValue: x => x.frontOfficerFullName
        }
    ]

    const pricingColumns = <T extends DealListItem>(): ColumnDescriptor<T>[] => [
        ...getCommentColumn('deal-final-pricing', 'pricingComments', undefined, 'pricingDetails'),
        {
            name: t('deals.label.pricing.unit_price'), groupedBy: 'pricingDetails',
            value: x => x.unitPrice, unit: x => x.indexPriceUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.pricing.premium'), groupedBy: 'pricingDetails',
            value: x => x.premium, unit: x => x.premiumUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.pricing.unit_price_plus_premium'), groupedBy: 'pricingDetails',
            value: x => x.unitPricePlusPremium, unit: x => x.indexPriceUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.pricing.quantity_unit_price'), groupedBy: 'pricingDetails',
            value: x => x.quantityUnitPrice, unit: x => x.quantityPriceUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.pricing.transport_fees'), groupedBy: 'pricingDetails',
            value: x => x.transportFees, unit: x => x.companyCurrency + '/' + x.productQuantitytUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.pricing.storage_fees'), groupedBy: 'pricingDetails',
            value: x => x.storageFees, unit: x => x.companyCurrency + '/' + x.productQuantitytUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.pricing.deal_unit_price'), defaultGroup: 'pricingDetails',
            value: x => x.dealUnitPrice, unit: x => x.quantityPriceUnit
        },
        {
            name: t('deals.label.pricing.exchange_rate'), groupedBy: 'pricingDetails',
            value: x => x.exchangeRate
        },
        {
            name: t('deals.label.pricing.amount'), groupedBy: 'pricingDetails',
            value: x => x.amount, unit: x => x.currency, textAlign: 'right',
            tooltipValue: x => t('deals.label.pricing.tooltip_amount')
        },
        {
            name: t('deals.label.pricing.amount_sap'), groupedBy: 'pricingDetails',
            value: x => x.amountSap, unit: x => x.currency, textAlign: 'right',
            tooltipValue: x => t('deals.label.pricing.tooltip_amount_sap')
        },
        {
            name: t('deals.label.pricing.payment_terms'), groupedBy: 'pricingDetails',
            value: x => x.paymentTerms
        },
        {
            name: t('deals.label.pricing.payment_terms_date'), groupedBy: 'pricingDetails',
            value: x => formatDate(x.paymentTermsDate)
        }
    ]

    const provisionalPricingColumns = <T extends DealListItem>(): ColumnDescriptor<T>[] => [
        ...getCommentColumn('deal-provisional-pricing', 'provPricingComments', undefined, 'provisionalPricingDetails'),
        {
            name: t('deals.label.provisional_pricing.unit_price'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalUnitPrice, unit: x => x.indexPriceUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.provisional_pricing.premium'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalPremium, unit: x => x.premiumUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.provisional_pricing.unit_price_plus_premium'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalUnitPricePlusPremium, unit: x => x.indexPriceUnit, textAlign: 'right',
        },
        {
            name: t('deals.label.provisional_pricing.quantity_unit_price'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalQuantityUnitPrice, unit: x => x.quantityPriceUnit, textAlign: 'right'
        },
        {
            name: t('deals.label.provisional_pricing.transport_fees'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalTransportFees, unit: x => x.companyCurrency + '/' + x.productQuantitytUnit,
            textAlign: 'right'
        },
        {
            name: t('deals.label.provisional_pricing.storage_fees'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalStorageFees, unit: x => x.companyCurrency + '/' + x.productQuantitytUnit,
            textAlign: 'right'
        },
        {
            name: t('deals.label.provisional_pricing.deal_unit_price'), defaultGroup: 'provisionalPricingDetails',
            value: x => x.provisionalDealUnitPrice, unit: x => x.quantityPriceUnit
        },
        {
            name: t('deals.label.provisional_pricing.exchange_rate'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalExchangeRate
        },
        {
            name: t('deals.label.provisional_pricing.amount'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalAmount, unit: x => x.currency, textAlign: 'right',
            tooltipValue: x => t('deals.label.provisional_pricing.tooltip_amount')
        },
        {
            name: t('deals.label.provisional_pricing.amount_sap'), groupedBy: 'provisionalPricingDetails',
            value: x => x.provisionalAmountSap, unit: x => x.currency, textAlign: 'right',
            tooltipValue: x => t('deals.label.provisional_pricing.tooltip_amount_sap')
        }
    ]

    const dealListColumns: ColumnDescriptor<DealListItem>[] = useMemo(() => [
        ...groupedHeaderColumns,
        {
            name: t('deals.label.volumeAbr'), defaultGroup: 'quantityDetails',
            value: x => x.volume, textAlign: 'right',
            htmlFor: x => formatCell(x.isQuantityInTolerance, x.volume), total: totalVolume,
        },
        {
            name: t('deals.label.movementQuantity'), groupedBy: 'quantityDetails',
            value: x => x.movementQuantity, textAlign: 'right',
            htmlFor: x => formatCell(x.isQuantityInTolerance, x.movementQuantity), total: totalMvtQuantity,
        },
        {
            name: t('deals.label.sapQuantityAbr'), groupedBy: 'quantityDetails',
            value: x => x.sapQuantity, total: totalSapQuantity, textAlign: 'right'
        },
        { name: t('deals.label.unit'), groupedBy: 'quantityDetails', value: x => x.unit },
        {
            name: t('deals.label.quantityAbr'), groupedBy: 'quantityDetails',
            value: x => x.quantity, textAlign: 'right',
            htmlFor: x => formatCell(x.isQuantityInTolerance, x.quantity), total: totalQuantity,
        },
        {
            name: t('deals.label.underTolerancePercentage'), groupedBy: 'quantityDetails',
            value: x => x.underTolerance, textAlign: 'right'
        },
        {
            name: t('deals.label.UnderToleranceQuantity'), groupedBy: 'quantityDetails',
            value: x => x.underToleranceQuantity, textAlign: 'right'
        },
        {
            name: t('deals.label.overTolerancePercentage'), groupedBy: 'quantityDetails',
            value: x => x.overTolerance, textAlign: 'right'
        },
        {
            name: t('deals.label.overToleranceQuantity'), groupedBy: 'quantityDetails',
            value: x => x.overToleranceQuantity, textAlign: 'right'
        },
        { name: t('deals.label.toleranceOption'), groupedBy: 'quantityDetails', value: x => x.toleranceOption },
        {
            name: t('deals.label.dealConsumption'), groupedBy: 'quantityDetails',
            value: x => x.dealConsumption != null ? (x.dealConsumption * 100).toFixed(2) : null
        },
        ...sapColumns(),
        { name: t('deals.label.pricingType'), defaultGroup: 'priceDetails', value: x => x.pricingType },
        {
            name: t('deals.label.formulaType'), groupedBy: 'priceDetails',
            value: x => x.formulaType !== null ? t(`admin.masterdata.pricingPeriod.formulaType.${x.formulaType}`).toString() : '',
            hidden: !hasFeatureFormulaType
        },
        { name: t('deals.label.currency'), groupedBy: 'priceDetails', value: x => x.currency },
        { name: t('deals.label.quantityPriceUnitAbr'), groupedBy: 'priceDetails', value: x => x.quantityPriceUnit },
        ...organizationnalColumns(),
        ...(hasFeatureGenericPricing ? pricingColumns() : []),
        ...(hasFeatureGenericPricing && hasFeatureProvitionalPricing ? provisionalPricingColumns() : [])
    ], [
        t, classes, groupedHeaderColumns,
        totalVolume, totalMvtQuantity, totalSapQuantity, totalQuantity,
        hasFeatureGenericPricing, hasFeatureProvitionalPricing
    ])

    const pricingPeriodColumns: ColumnDescriptor<PricingPeriodListItem>[] =
        useMemo<ColumnDescriptor<PricingPeriodListItem>[]>(() => [
            ...headerColumns as ColumnDescriptor<PricingPeriodListItem>[],
            {
                name: t('deals.label.priceIndexName'),
                value: x => x.priceIndexName, wrapTextMax: true
            },
            {
                name: t('deals.label.pricingPeriod'),
                value: x => formatFromToDate(x.pricingStartDate) + ' -> ' + formatFromToDate(x.pricingEndDate)
            },
            {
                name: t('deals.label.formulaType'),
                value: x => x.formulaType ? t(`admin.masterdata.pricingPeriod.formulaType.${x.formulaType}`).toString() : '',
                hidden: !hasFeatureFormulaType
            },
            {
                name: t('deals.label.premium'),
                value: x => x.premium, wrapTextMax: true, unit: x => x.premiumUnit, textAlign: 'right'
            },
            {
                name: t('deals.label.pricedDealVolume'), defaultGroup: 'quantityDetails',
                htmlFor: x => formatCell(x.isQuantityInTolerance, x.pricedDealVolume), wrapTextMax: true,
                value: x => x.pricedDealVolume,
                total: totalPricedDealVolume, textAlign: 'right'
            },
            {
                name: t('deals.label.pricedMovementVolume'), groupedBy: 'quantityDetails',
                htmlFor: x => formatCell(x.isQuantityInTolerance, x.pricedMovementVolume), wrapTextMax: true,
                value: x => x.pricedMovementVolume,
                total: totalPricedMovementVolume, textAlign: 'right'
            },
            {
                name: t('deals.label.pricedSapQuantity'), groupedBy: 'quantityDetails',
                value: x => x.pricedSapQuantity, wrapTextMax: true,
                total: totalPricedSapQuantity, textAlign: 'right'
            },
            {
                name: t('deals.label.pricedDealQuantity'), groupedBy: 'quantityDetails',
                htmlFor: x => formatCell(x.isQuantityInTolerance, x.pricedDealQuantity), wrapTextMax: true,
                value: x => x.pricedDealQuantity,
                total: totalPricedDealQuantity, textAlign: 'right'
            },
            {
                name: t('deals.label.pricingPercentage'), groupedBy: 'quantityDetails',
                value: x => x.percentage, wrapTextMax: true
            },
            ...sapColumns(),
            ...organizationnalColumns()
        ], [
            t, classes, headerColumns,
            totalPricedDealVolume, totalPricedMovementVolume, totalPricedSapQuantity, totalPricedDealQuantity
        ])

    return {
        dealListColumns,
        pricingPeriodColumns
    }
}